let echartsPromise = null;

function loadEcharts() {
  if (!echartsPromise) {
    echartsPromise = (async () => {
      const [core, { LineChart }, components, { CanvasRenderer }] = await Promise.all([
        import("echarts/core"),
        import("echarts/charts"),
        import("echarts/components"),
        import("echarts/renderers"),
      ]);

      core.use([
        LineChart,
        components.GridComponent,
        components.TooltipComponent,
        components.LegendComponent,
        CanvasRenderer,
      ]);

      return core;
    })();
  }
  return echartsPromise;
}

export const EChartsHook = {
  async mounted() {
    // Register the event handler SYNCHRONOUSLY, before awaiting the lazy
    // echarts chunk. Small trackers (consoles: 22 products) push chart_data
    // faster than the chunk loads; a handler registered after the await
    // misses that first push and the chart stays blank until the next
    // user-triggered push (the "no graph on consoles" production bug).
    this.pendingData = null;
    const eventName = this.el.dataset.chartEvent || "chart_data";
    this.handleEvent(eventName, (data) => {
      if (this.chart) {
        this.renderChart(data);
      } else {
        this.pendingData = data;
      }
    });

    try {
      const echarts = await loadEcharts();
      this.chart = echarts.init(this.el, null, { renderer: "canvas" });
      this.isMobile = this.el.clientWidth < 640;

      this.resizeObserver = new ResizeObserver(() => {
        this.isMobile = this.el.clientWidth < 640;
        this.chart.resize();
      });
      this.resizeObserver.observe(this.el);

      if (this.pendingData) {
        this.renderChart(this.pendingData);
        this.pendingData = null;
      }
    } catch (e) {
      console.error("Failed to load chart library:", e);
    }
  },

  renderChart(data) {
    if (!this.chart || !data || !data.dates || !data.series) return;

    const currencySymbol = data.currency_symbol || "£";
    const isMobile = this.isMobile;

    // Print-tone series palette anchored on the design tokens:
    // drop, ink, moss, red, mustard, plum, slate teal, sienna, muted.
    const PALETTE = [
      "#E85D04", "#17150F", "#5C7A4A", "#B91C1C", "#C08A00",
      "#7C3A5F", "#3E6B6B", "#A0653B", "#6B675E",
    ];
    const MONO = "Spline Sans Mono, ui-monospace, monospace";

    const option = {
      color: PALETTE,
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(23, 21, 15, 0.95)",
        borderColor: "rgba(107, 103, 94, 0.5)",
        borderWidth: 1,
        textStyle: {
          color: "#FBFAF8",
          fontSize: 12,
          fontFamily: MONO,
        },
        formatter: function (params) {
          let date = params[0].axisValue;
          let lines = params
            .filter((p) => p.value != null)
            .map((p) => {
              const dot = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px;"></span>`;
              return `${dot}${p.seriesName}: <strong>${currencySymbol}${Number(p.value).toFixed(2)}</strong>`;
            })
            .join("<br/>");
          return `<div style="font-weight:600;margin-bottom:4px;">${date}</div>${lines}`;
        },
      },
      legend: Object.assign(
        {
          type: "scroll",
          data: data.series.map((s) => s.name),
          textStyle: { color: "#6B675E", fontSize: isMobile ? 10 : 11, fontFamily: MONO },
          icon: "roundRect",
          itemWidth: 12,
          itemHeight: 3,
          pageIconSize: 10,
          pageTextStyle: { color: "#6B675E", fontSize: 10 },
        },
        isMobile
          ? { top: 0, left: 0, right: 0 }
          : { bottom: 0 },
      ),
      grid: {
        left: 12,
        right: 12,
        top: isMobile ? 28 : 12,
        bottom: isMobile ? 24 : 36,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: data.dates,
        axisLine: { lineStyle: { color: "#E5E2DB" } },
        axisLabel: {
          color: "#6B675E",
          fontSize: 10,
          fontFamily: MONO,
          interval: isMobile ? "auto" : null,
          rotate: isMobile ? 30 : 0,
          formatter: function (value) {
            const d = new Date(value);
            const months = [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ];
            return months[d.getMonth()] + " " + d.getFullYear().toString().slice(2);
          },
        },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: "#E5E2DB", opacity: 0.6 } },
        axisLabel: {
          color: "#6B675E",
          fontSize: 10,
          fontFamily: MONO,
          formatter: function (value) {
            return currencySymbol + value.toFixed(0);
          },
        },
      },
      series: data.series.map((s) => ({
        name: s.name,
        type: "line",
        smooth: true,
        symbol: "none",
        lineStyle: { width: 2 },
        data: s.data,
        connectNulls: true,
      })),
    };

    this.chart.setOption(option, true);
  },

  reconnected() {
    // Re-init chart after WebSocket reconnect if it was disposed
    if (!this.chart) {
      loadEcharts().then((echarts) => {
        this.chart = echarts.init(this.el, null, { renderer: "canvas" });
        this.isMobile = this.el.clientWidth < 640;
        this.resizeObserver = new ResizeObserver(() => {
          this.isMobile = this.el.clientWidth < 640;
          this.chart.resize();
        });
        this.resizeObserver.observe(this.el);
      }).catch((e) => {
        console.error("Failed to load chart library on reconnect:", e);
      });
    }
  },

  disconnected() {
    this._cleanup();
  },

  destroyed() {
    this._cleanup();
  },

  _cleanup() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
};
